import { Save } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import {
    QueryObserverResult,
    RefetchOptions,
    RefetchQueryFilters,
} from "@tanstack/react-query";
import React, { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";

import { DocumentInfo } from "../../../../../../../../models/documents";
import { deleteBudgetDocument } from "../../../../../../../../services/budget";
import { TReadDocumentsDataResponse } from "../../../../../../../../services/budget/read-budget-documents";
import DeleteTemplateDialog from "./DeleteTemplateDialog";
import TemplateList from "./TemplateList";

interface ITemplateManagerProps {
    documentInfo: DocumentInfo & Partial<{ documentId?: string; date: string }>;
    documentsData: TReadDocumentsDataResponse;
    refetchDocuments: <TPageData>(
        options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
    ) => Promise<QueryObserverResult<TReadDocumentsDataResponse, unknown>>;
    onApplyTemplate: (documentId: string) => void;
    onSaveDocument: () => void;
}

const TemplateManager: React.FC<ITemplateManagerProps> = ({
    documentInfo,
    documentsData,
    onApplyTemplate,
    onSaveDocument,
    refetchDocuments,
}) => {
    const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
    const selectedDocumentToDelete = useRef<string>();

    const handleDeleteOpenDialog = useCallback(
        (documentId: string) => {
            selectedDocumentToDelete.current = documentId;
            setIsOpenDeleteDialog((prev) => !prev);
        },
        [documentInfo, isOpenDeleteDialog, selectedDocumentToDelete.current]
    );

    const handleDeleteCloseDialog = useCallback(() => {
        selectedDocumentToDelete.current = undefined;
        setIsOpenDeleteDialog((prev) => !prev);
    }, [documentInfo, isOpenDeleteDialog, selectedDocumentToDelete.current]);

    const onDeleteTemplate = useCallback(async () => {
        if (selectedDocumentToDelete.current) {
            await deleteBudgetDocument({
                documentId: selectedDocumentToDelete.current,
            })
                .then(() => toast.success("Documento excluido com sucesso!"))
                .catch((erro) =>
                    toast.error(`Erro ao excluir documento: ${erro}`)
                );
            await refetchDocuments();
        }
        handleDeleteCloseDialog();
    }, [documentsData, documentInfo, isOpenDeleteDialog]);

    const templates = documentsData ?? [];
    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    alignItems: "center",
                }}
            >
                <Button
                    variant="outlined"
                    startIcon={<Save />}
                    onClick={onSaveDocument}
                >
                    Salvar Documento
                </Button>

                <TemplateList
                    documentInfo={documentInfo}
                    templates={templates}
                    onApplyTemplate={onApplyTemplate}
                    onDeleteTemplate={handleDeleteOpenDialog}
                />
            </Box>
            <DeleteTemplateDialog
                open={isOpenDeleteDialog}
                handleClose={handleDeleteCloseDialog}
                handleDelete={onDeleteTemplate}
            />
        </>
    );
};

export default TemplateManager;
